import type { ComponentProps, PropsWithChildren } from 'react'

const LODGING_OFFER_ITEM_TYPE = 'https://schema.org/Offer https://schema.org/LodgingReservation'

type AccommodationMicrodataProps = Omit<
  ComponentProps<'div'>,
  'className' | 'itemScope' | 'itemType'
> & { accommodationId: number }

export function AccommodationMicrodata({
  accommodationId,
  children,
  ...attributes
}: AccommodationMicrodataProps) {
  return (
    <div {...attributes} className="contents" itemScope itemType="https://schema.org/Hotel">
      <meta itemProp="identifier" content={String(accommodationId)} />
      {children}
    </div>
  )
}

export type LodgingStay = {
  checkin: string
  checkout: string
  adults: number
  childCount: number
}

type AccommodationOfferMicrodataProps = PropsWithChildren<LodgingStay & { taxIncluded?: boolean }>

export function AccommodationOfferMicrodata({
  checkin,
  checkout,
  adults,
  childCount,
  taxIncluded,
  children,
}: AccommodationOfferMicrodataProps) {
  return (
    <span className="contents" itemProp="makesOffer" itemScope itemType={LODGING_OFFER_ITEM_TYPE}>
      <LodgingStayMeta
        checkin={checkin}
        checkout={checkout}
        adults={adults}
        childCount={childCount}
      />
      <span
        className="contents"
        itemProp="priceSpecification"
        itemScope
        itemType="https://schema.org/CompoundPriceSpecification"
      >
        {taxIncluded && <meta itemProp="valueAddedTaxIncluded" content="true" />}
        {children}
      </span>
    </span>
  )
}

export function AccommodationSoldOutMicrodata(stay: LodgingStay) {
  return (
    <span className="contents" itemProp="makesOffer" itemScope itemType={LODGING_OFFER_ITEM_TYPE}>
      <LodgingStayMeta {...stay} />
      <meta itemProp="availability" content="https://schema.org/SoldOut" />
    </span>
  )
}

function LodgingStayMeta({ checkin, checkout, adults, childCount }: LodgingStay) {
  return (
    <>
      <meta itemProp="checkinTime" content={checkin} />
      <meta itemProp="checkoutTime" content={checkout} />
      <meta itemProp="numAdults" content={String(adults)} />
      <meta itemProp="numChildren" content={String(childCount)} />
    </>
  )
}
